import { ArrowLeft } from "lucide-react"

import RatingStars from "@/assets/img/soulmate/rating-stars.png"
import StarGold from "@/assets/img/soulmate/star-gold.svg"
import TopGlow from "@/assets/img/soulmate/top-glow.png"
import WreathLeft from "@/assets/img/soulmate/wreath-left.png"
import WreathRight from "@/assets/img/soulmate/wreath-right.png"
import Logo from "@/assets/img/astro-map/logo.png"
import { Button } from "@/components/ui/button"
import type { StepComponentProps } from "./step-contract"

/** Social-proof reviews (Figma Step0.1). */
const REVIEWS = [
  {
    text: "I moved to Lisbon after my reading, and for the first time in years, it felt like I could finally breathe again. The energy matched me perfectly.",
    name: "Olivia",
    rating: "4.9",
  },
  {
    text: "I opened my map just for fun, but the insights about where I thrive emotionally were shockingly real.",
    name: "Venuslines",
    rating: "4.8",
  },
]

/** Leading part of the headline shown in main-purple, per the design. */
const TITLE_HIGHLIGHT = "25 Million+ people"

function ReviewStars() {
  return (
    <span className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <img key={i} src={StarGold} alt="" className="size-4" />
      ))}
    </span>
  )
}

/**
 * Astro Map social-proof interstitial (Figma Step0.1): logo, trust headline and a
 * dark-surface reviews card — a wreath rating header plus a horizontally
 * scrollable review slider — then Continue. Sits before the counted quiz, so it
 * renders its own back-only navbar with no step counter.
 */
export function AstroSocialProofStep({ step, onAdvance, onBack }: StepComponentProps) {
  return (
    <div className="relative flex flex-1 flex-col overflow-hidden bg-dark-base text-purple-light">
      <img
        src={TopGlow}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px] w-full object-cover"
      />

      <div className="relative flex flex-1 flex-col">
        <div className="flex h-14 items-center px-4">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            className="-ml-2 flex size-10 items-center justify-center rounded-full text-purple-light transition-colors hover:bg-purple-light/10"
          >
            <ArrowLeft className="size-6" />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-6 px-4">
          <div className="flex flex-col items-center gap-4">
            <img src={Logo} alt="" className="size-22 rounded-[25px]" />
            <h1 className="text-center font-display text-2xl font-medium text-purple-light">
              {step.title.startsWith(TITLE_HIGHLIGHT) ? (
                <>
                  <span className="text-main-purple">{TITLE_HIGHLIGHT}</span>
                  {step.title.slice(TITLE_HIGHLIGHT.length)}
                </>
              ) : (
                step.title
              )}
            </h1>
          </div>

          <div className="flex flex-col gap-4 rounded-[24px] bg-dark-surface px-4 pt-4 pb-6">
            <div className="flex items-center justify-center gap-2">
              <img src={WreathLeft} alt="" className="h-20 w-[34px]" />
              <div className="flex flex-col items-center gap-1">
                <span className="font-display text-xl font-medium text-purple-light">
                  Our users trust us
                </span>
                <img src={RatingStars} alt="" className="h-6 w-auto" />
                <span className="text-xs text-purple-light/40">more than 10k reviews</span>
              </div>
              <img src={WreathRight} alt="" className="h-20 w-[34px]" />
            </div>

            <div className="no-scrollbar flex gap-4 overflow-x-auto">
              {REVIEWS.map((review) => (
                <div
                  key={review.name}
                  className="flex w-[300px] shrink-0 flex-col gap-6 rounded-2xl border border-purple-light/20 p-4"
                >
                  <p className="text-base text-purple-light/70">{review.text}</p>
                  {/* Cards stretch to a common height, so pin the name/rating to the
                      bottom — otherwise it trails a short review and the two cards'
                      names sit at different heights. */}
                  <div className="mt-auto flex flex-col gap-1">
                    <span className="font-display text-base font-medium text-purple-light">
                      {review.name}
                    </span>
                    <span className="flex items-center gap-2">
                      <ReviewStars />
                      <span className="text-sm font-medium text-gold">{review.rating}</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-4 pt-2 pb-6">
          <Button variant="gradient" size="xl" onClick={onAdvance}>
            {step.cta ?? "Continue"}
          </Button>
        </div>
      </div>
    </div>
  )
}
